import { useCallback, useMemo, useState } from "react";
import { fieldErrorsForAliases, logCmtsSafeError, normalizeCmtsApiError, type CmtsApiError, type CmtsApiErrorContext, type CmtsDependencyError } from "@cmts/errors/index.js";

export type UseApiErrorResult = {
  readonly error: CmtsApiError | null;
  readonly fieldErrors: Record<string, string[]>;
  readonly dependencyErrors: CmtsDependencyError[];
  readonly setError: (error: unknown, context?: CmtsApiErrorContext | undefined) => CmtsApiError;
  readonly clearError: () => void;
  readonly hasFieldError: (fieldName: string) => boolean;
  readonly getFieldErrors: (fieldName: string, aliases?: readonly string[] | undefined) => string[];
  readonly isRetryable: boolean;
  readonly isBlocking: boolean;
  readonly correlationId?: string | undefined;
};

export function useApiError(initialError?: unknown, initialContext?: CmtsApiErrorContext | undefined): UseApiErrorResult {
  const [error, setNormalizedError] = useState<CmtsApiError | null>(() => initialError === undefined ? null : normalizeCmtsApiError(initialError, initialContext));

  const setError = useCallback((input: unknown, context?: CmtsApiErrorContext | undefined): CmtsApiError => {
    const normalized = logCmtsSafeError(input, context);
    setNormalizedError(normalized);
    return normalized;
  }, []);

  const clearError = useCallback((): void => {
    setNormalizedError(null);
  }, []);

  const getFieldErrors = useCallback((fieldName: string, aliases: readonly string[] = []): string[] => {
    return fieldErrorsForAliases(error?.fieldErrors ?? {}, fieldName, aliases);
  }, [error]);

  const hasFieldError = useCallback((fieldName: string): boolean => getFieldErrors(fieldName).length > 0, [getFieldErrors]);

  return useMemo(() => ({
    error,
    fieldErrors: error?.fieldErrors ?? {},
    dependencyErrors: error?.dependencyErrors ?? [],
    setError,
    clearError,
    hasFieldError,
    getFieldErrors,
    isRetryable: Boolean(error?.retryable),
    isBlocking: error?.severity === "blocking",
    ...(error?.correlationId ? { correlationId: error.correlationId } : {}),
  }), [clearError, error, getFieldErrors, hasFieldError, setError]);
}

export { normalizeCmtsApiError, fieldErrorsForAliases } from "@cmts/errors/index.js";
